<script setup>
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import FloatLabel from "primevue/floatlabel";
import Select from "primevue/select";
import { useAsync } from "@/composables/api/useAsync.js";
import { useAgent } from "@/composables/model/agent/useAgent.js";
import { agentOptionLabel } from "@/utils/agent.js";

const props = defineProps({
  modelValue: { type: [Number, String], default: null },
  label: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  inputId: { type: String, default: "agent" },
  selectedProfile: { type: Object, default: null },
});

const emit = defineEmits(["update:modelValue"]);

const { t } = useI18n();
const { agents, loadAgents } = useAgent();
const { execute, isLoading, isError, errorMessage } = useAsync(loadAgents, {
  apiResult: true,
});

const options = computed(() => {
  const list = agents.value || [];
  const current = props.selectedProfile;
  if (current?.id == null) return list;
  if (list.some((profile) => Number(profile.id) === Number(current.id)))
    return list;
  return [current, ...list];
});

onMounted(() => {
  execute();
});
</script>

<template>
  <div class="flex flex-col gap-0.5">
    <FloatLabel class="w-full custom-select" variant="on">
      <Select
        :modelValue="modelValue"
        :options="options"
        :optionLabel="agentOptionLabel"
        optionValue="id"
        :inputId="inputId"
        :loading="isLoading"
        :disabled="disabled || isLoading"
        showClear
        filter
        autoFilterFocus
        class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
        input-class="!text-xs 2xl:!text-sm"
        overlayClass="custom-select-overlay"
        @update:modelValue="emit('update:modelValue', $event)"
      />
      <label :for="inputId" class="!bg-slate-900">{{ label }}</label>
    </FloatLabel>
    <p v-if="isError" class="text-red-300 mt-1 text-xs">{{ errorMessage }}</p>
    <button
      v-if="isError"
      type="button"
      class="text-xs text-slate-300 text-start"
      @click="execute()"
    >
      {{ t("customerSession.retry") }}
    </button>
  </div>
</template>
